import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Trash2,
  Printer,
  Save,
  ArrowLeft,
  Loader2,
  Search,
  FileText,
  Scale,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtCurrency = (n) => `R ${Number(n || 0).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-ZA");
};

const emptyRow = () => ({
  product_id: null,
  plu_code: "",
  name: "",
  qty: 1,
  price_incl: 0,
  _searchOpen: false,
  _searchResults: [],
  _searching: false,
  _searchQuery: "",
});

const Editor = ({ initialSheet, onBack, onSaved }) => {
  const [recipient, setRecipient] = useState(initialSheet?.recipient_name || "Other Shop");
  const [notes, setNotes] = useState(initialSheet?.notes || "");
  const [rows, setRows] = useState(() => {
    if (initialSheet?.items?.length) {
      return initialSheet.items.map((it) => ({
        product_id: it.product_id || null,
        plu_code: it.plu_code || "",
        name: it.name || "",
        qty: it.qty || 0,
        price_incl: it.price_incl || 0,
        _searchOpen: false,
        _searchResults: [],
        _searching: false,
        _searchQuery: "",
      }));
    }
    return [emptyRow()];
  });
  const [saving, setSaving] = useState(false);
  const [sheet, setSheet] = useState(initialSheet || null);
  const searchTimers = useRef({});

  const total = rows.reduce(
    (sum, r) => sum + Number(r.qty || 0) * Number(r.price_incl || 0),
    0
  );

  const updateRow = (idx, patch) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const removeRow = (idx) => {
    setRows((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));
  };

  const addRow = () => {
    setRows((prev) => [...prev, emptyRow()]);
  };

  const runProductSearch = useCallback((idx, query) => {
    if (searchTimers.current[idx]) clearTimeout(searchTimers.current[idx]);
    searchTimers.current[idx] = setTimeout(async () => {
      const q = (query || "").trim();
      if (q.length < 2) {
        updateRow(idx, { _searchResults: [], _searching: false });
        return;
      }
      updateRow(idx, { _searching: true });
      try {
        const res = await axios.get(`${API}/products/search`, { params: { q } });
        updateRow(idx, { _searchResults: res.data || [], _searching: false });
      } catch (e) {
        updateRow(idx, { _searchResults: [], _searching: false });
      }
    }, 220);
  }, []);

  const pickProduct = (idx, product) => {
    updateRow(idx, {
      product_id: product.id,
      plu_code: product.plu_code || "",
      name: product.name || "",
      price_incl: Number(product.retail_price || product.price || 0),
      _searchOpen: false,
      _searchResults: [],
      _searchQuery: "",
    });
  };

  const handleSave = async () => {
    const validRows = rows.filter(
      (r) => (r.name || "").trim() && Number(r.qty) > 0
    );
    if (validRows.length === 0) {
      toast.error("Add at least one item with a name and quantity.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        recipient_name: recipient.trim() || "Other Shop",
        notes: notes.trim(),
        items: validRows.map((r) => ({
          product_id: r.product_id || null,
          plu_code: (r.plu_code || "").toString(),
          name: r.name.trim(),
          qty: Number(r.qty),
          price_incl: Number(r.price_incl || 0),
        })),
      };
      let saved;
      if (sheet?.id) {
        const res = await axios.put(`${API}/internal-splits/${sheet.id}`, payload);
        saved = res.data;
        toast.success(`Updated ${saved.sheet_number}`);
      } else {
        const res = await axios.post(`${API}/internal-splits`, payload);
        saved = res.data;
        toast.success(`Saved as ${saved.sheet_number}`);
      }
      setSheet(saved);
      if (onSaved) onSaved(saved);
      return saved;
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndPrint = async () => {
    const saved = await handleSave();
    if (saved) {
      // small delay so toast renders, then print
      setTimeout(() => doPrint(saved), 150);
    }
  };

  const doPrint = (s) => {
    const data = s || sheet || {
      sheet_number: "DRAFT",
      recipient_name: recipient,
      items: rows
        .filter((r) => (r.name || "").trim() && Number(r.qty) > 0)
        .map((r) => ({
          name: r.name,
          plu_code: r.plu_code,
          qty: r.qty,
          price_incl: r.price_incl,
          line_total: Number(r.qty || 0) * Number(r.price_incl || 0),
        })),
      total,
      notes,
      created_at: new Date().toISOString(),
    };
    const win = window.open("", "_blank", "width=900,height=1100");
    if (!win) {
      toast.error("Pop-up blocked. Allow pop-ups for this site to print.");
      return;
    }
    const html = `
<!DOCTYPE html>
<html>
<head>
  <title>${data.sheet_number} — INTERNAL SPLIT</title>
  <style>
    @page { size: A4; margin: 16mm; }
    body { font-family: Arial, Helvetica, sans-serif; color: #111; }
    h1 { font-size: 22px; margin: 0 0 4px 0; letter-spacing: 1px; }
    .sub { font-size: 12px; color: #555; margin-bottom: 18px; }
    .meta { display:flex; justify-content: space-between; margin-bottom: 18px; font-size: 13px; }
    .meta b { display:block; color:#000; font-size: 14px; }
    table { width: 100%; border-collapse: collapse; margin-top: 8px; }
    th, td { border-bottom: 1px solid #ddd; padding: 8px 10px; text-align: left; font-size: 13px; }
    th { background: #f5f5f5; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; }
    td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
    tfoot td { border-top: 2px solid #000; border-bottom: none; font-weight: bold; font-size: 16px; padding-top: 14px; }
    .notes { margin-top: 24px; font-size: 12px; color: #555; border-top: 1px dashed #ccc; padding-top: 10px; white-space: pre-wrap; }
    .signature { margin-top: 50px; display: flex; justify-content: space-between; gap: 40px; font-size: 12px; color: #555; }
    .sig-block { flex: 1; border-top: 1px solid #000; padding-top: 6px; }
    @media print { .no-print { display: none; } }
  </style>
</head>
<body>
  <h1>INTERNAL SPLIT — STOCK TRANSFERRED TO ${escapeHtml(data.recipient_name || "Other Shop").toUpperCase()}</h1>
  <div class="sub">This is an internal balancing sheet — not a sales invoice. No VAT entry, not a tax invoice.</div>
  <div class="meta">
    <div><b>Sheet #</b>${escapeHtml(data.sheet_number)}</div>
    <div><b>Date</b>${escapeHtml(fmtDate(data.created_at))}</div>
    <div><b>Recipient</b>${escapeHtml(data.recipient_name || "Other Shop")}</div>
  </div>
  <table>
    <thead>
      <tr>
        <th style="width:80px">PLU</th>
        <th>Description</th>
        <th class="num" style="width:80px">Qty</th>
        <th class="num" style="width:110px">Price Incl</th>
        <th class="num" style="width:120px">Line Total</th>
      </tr>
    </thead>
    <tbody>
      ${(data.items || [])
        .map(
          (r) => `<tr>
        <td>${escapeHtml(r.plu_code || "")}</td>
        <td>${escapeHtml(r.name || "")}</td>
        <td class="num">${Number(r.qty || 0)}</td>
        <td class="num">${fmtCurrency(r.price_incl)}</td>
        <td class="num">${fmtCurrency(Number(r.qty || 0) * Number(r.price_incl || 0))}</td>
      </tr>`
        )
        .join("")}
    </tbody>
    <tfoot>
      <tr>
        <td colspan="4" class="num">TOTAL OWED BY ${escapeHtml(data.recipient_name || "Other Shop").toUpperCase()}</td>
        <td class="num">${fmtCurrency(data.total)}</td>
      </tr>
    </tfoot>
  </table>
  ${data.notes ? `<div class="notes"><b>Notes:</b>\n${escapeHtml(data.notes)}</div>` : ""}
  <div class="signature">
    <div class="sig-block">Prepared by</div>
    <div class="sig-block">Received by (${escapeHtml(data.recipient_name || "Other Shop")})</div>
  </div>
  <div class="no-print" style="margin-top:30px;text-align:center">
    <button onclick="window.print()" style="padding:8px 18px;font-size:14px;cursor:pointer">Print</button>
  </div>
</body>
</html>`;
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 300);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Button variant="ghost" onClick={onBack} data-testid="split-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to list
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => doPrint(null)} data-testid="split-print-draft-btn">
            <Printer className="h-4 w-4 mr-1" />
            Print preview
          </Button>
          <Button onClick={handleSave} disabled={saving} variant="outline" data-testid="split-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            Save
          </Button>
          <Button onClick={handleSaveAndPrint} disabled={saving} className="bg-purple-600 hover:bg-purple-700" data-testid="split-save-print-btn">
            <Printer className="h-4 w-4 mr-1" />
            Save & Print
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Scale className="h-4 w-4 text-purple-600" />
            {sheet ? `Editing ${sheet.sheet_number}` : "New Split Sheet"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="recipient">Recipient (the other shop)</Label>
              <Input
                id="recipient"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                placeholder="e.g. Shop Next Door"
                data-testid="split-recipient-input"
              />
            </div>
            <div>
              <Label htmlFor="notes">Notes (optional, prints on the sheet)</Label>
              <Input
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. From Coke order Apr 18"
                data-testid="split-notes-input"
              />
            </div>
          </div>

          <div className="border rounded-md overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[260px]">Product</TableHead>
                  <TableHead className="w-[110px]">PLU</TableHead>
                  <TableHead className="w-[110px]">Qty</TableHead>
                  <TableHead className="w-[140px] text-right">Price Incl</TableHead>
                  <TableHead className="w-[140px] text-right">Line Total</TableHead>
                  <TableHead className="w-[44px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, idx) => {
                  const lineTotal = Number(row.qty || 0) * Number(row.price_incl || 0);
                  return (
                    <TableRow key={idx} data-testid={`split-row-${idx}`}>
                      <TableCell>
                        <div className="relative">
                          <div className="flex">
                            <Input
                              value={row.name}
                              onChange={(e) => {
                                updateRow(idx, { name: e.target.value, _searchQuery: e.target.value, _searchOpen: true });
                                runProductSearch(idx, e.target.value);
                              }}
                              onFocus={() => updateRow(idx, { _searchOpen: true })}
                              onBlur={() =>
                                setTimeout(() => updateRow(idx, { _searchOpen: false }), 180)
                              }
                              placeholder="Search stock or type free-text…"
                              className="text-sm"
                              data-testid={`split-row-name-${idx}`}
                            />
                          </div>
                          {row._searchOpen && (row._searching || row._searchResults.length > 0) && (
                            <div className="absolute z-20 left-0 right-0 mt-1 max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
                              {row._searching && (
                                <div className="px-3 py-2 text-xs text-gray-500 flex items-center gap-2">
                                  <Loader2 className="h-3 w-3 animate-spin" /> searching…
                                </div>
                              )}
                              {row._searchResults.slice(0, 8).map((p) => (
                                <button
                                  key={p.id}
                                  type="button"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    pickProduct(idx, p);
                                  }}
                                  className="block w-full text-left px-3 py-2 text-sm hover:bg-purple-50 border-b last:border-b-0"
                                >
                                  <div className="font-medium">{p.name}</div>
                                  <div className="text-xs text-gray-500">
                                    PLU {p.plu_code || "—"}{p.retail_price ? ` · R${Number(p.retail_price).toFixed(2)}` : ""}
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Input
                          value={row.plu_code}
                          onChange={(e) => updateRow(idx, { plu_code: e.target.value })}
                          className="text-sm"
                          data-testid={`split-row-plu-${idx}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="any"
                          value={row.qty}
                          onChange={(e) => updateRow(idx, { qty: e.target.value })}
                          className="text-sm text-right"
                          data-testid={`split-row-qty-${idx}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          value={row.price_incl}
                          onChange={(e) => updateRow(idx, { price_incl: e.target.value })}
                          className="text-sm text-right"
                          data-testid={`split-row-price-${idx}`}
                        />
                      </TableCell>
                      <TableCell className="text-right font-mono">{fmtCurrency(lineTotal)}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeRow(idx)}
                          disabled={rows.length === 1}
                          data-testid={`split-row-remove-${idx}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={addRow} data-testid="split-add-row-btn">
              <Plus className="h-4 w-4 mr-1" />
              Add line
            </Button>
            <div className="text-right">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Total owed</div>
              <div className="text-2xl font-bold text-purple-700" data-testid="split-grand-total">
                {fmtCurrency(total)}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const escapeHtml = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const InternalSplits = () => {
  const [view, setView] = useState("list"); // 'list' | 'edit'
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSheet, setActiveSheet] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [query, setQuery] = useState("");

  const fetchSheets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/internal-splits`);
      setSheets(res.data?.sheets || []);
    } catch (e) {
      toast.error("Failed to load split sheets");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSheets();
  }, [fetchSheets]);

  const filteredSheets = sheets.filter((s) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      (s.sheet_number || "").toLowerCase().includes(q) ||
      (s.recipient_name || "").toLowerCase().includes(q)
    );
  });

  const openSheet = async (id) => {
    try {
      const res = await axios.get(`${API}/internal-splits/${id}`);
      setActiveSheet(res.data);
      setView("edit");
    } catch (e) {
      toast.error("Failed to open sheet");
    }
  };

  const newSheet = () => {
    setActiveSheet(null);
    setView("edit");
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/internal-splits/${id}`);
      toast.success("Sheet deleted");
      setConfirmDelete(null);
      fetchSheets();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  if (view === "edit") {
    return (
      <div className="container mx-auto p-4">
        <Editor
          initialSheet={activeSheet}
          onBack={() => {
            setView("list");
            fetchSheets();
          }}
          onSaved={() => fetchSheets()}
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Scale className="h-6 w-6 text-purple-600" />
            Internal Split Sheets
          </h1>
          <p className="text-sm text-gray-600 mt-1 max-w-xl">
            Track stock split with the shop next door — not a sale. Pick products, set qty &amp; price-incl, and print an A4 sheet to attach to the supplier invoice.
          </p>
        </div>
        <Button onClick={newSheet} className="bg-purple-600 hover:bg-purple-700" data-testid="split-new-btn">
          <Plus className="h-4 w-4 mr-1" />
          New Split Sheet
        </Button>
      </div>

      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by sheet # or recipient…"
                className="pl-8"
                data-testid="split-list-search"
              />
            </div>
            <div className="text-sm text-gray-500">
              {filteredSheets.length} of {sheets.length}
            </div>
          </div>

          {loading ? (
            <div className="py-12 text-center text-gray-500">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
              Loading…
            </div>
          ) : filteredSheets.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              <FileText className="h-10 w-10 mx-auto mb-2 text-gray-300" />
              {sheets.length === 0
                ? "No split sheets yet. Click \"New Split Sheet\" to create your first one."
                : "No sheets match your search."}
            </div>
          ) : (
            <div className="border rounded-md overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Sheet #</TableHead>
                    <TableHead>Recipient</TableHead>
                    <TableHead className="text-right">Items</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="w-[120px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSheets.map((s) => (
                    <TableRow key={s.id} data-testid={`split-list-row-${s.id}`}>
                      <TableCell className="font-mono font-medium">{s.sheet_number}</TableCell>
                      <TableCell>{s.recipient_name}</TableCell>
                      <TableCell className="text-right">{s.item_count}</TableCell>
                      <TableCell className="text-right font-mono font-bold text-purple-700">
                        {fmtCurrency(s.total)}
                      </TableCell>
                      <TableCell className="text-xs text-gray-600">{fmtDate(s.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openSheet(s.id)}
                            data-testid={`split-open-${s.id}`}
                          >
                            Open
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setConfirmDelete(s)}
                            data-testid={`split-delete-${s.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this split sheet?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.sheet_number} — {confirmDelete?.recipient_name} — {fmtCurrency(confirmDelete?.total)}
              <br />
              This action can&apos;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDelete && handleDelete(confirmDelete.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default InternalSplits;
